import { useUIManager } from "@/stores/ui-store";
import { BaseExtension } from "@/wc/text-editor/lib/core/base-extension";
import "@shoelace-style/shoelace/dist/components/alert/alert.js";
import { unsafeCSS } from "lit";
import { debounce, DebouncedFunc } from "lodash";
import { MermaidConfig } from "mermaid";
import panzoom from "panzoom";
import { Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";
import styles from "./styles.scss?inline";

const pluginKey = new PluginKey("mermaid");

// Improved rendering with size preservation
async function renderMermaidDiagram(
    source: string,
    container: HTMLElement,
    mermaidConfig: MermaidConfig
) {
    try {
        // Measure current size
        const currentHeight = container.offsetHeight;
        const currentWidth = container.offsetWidth;

        // Set minimum dimensions while rendering
        container.style.minHeight = `${currentHeight}px`;
        container.style.minWidth = `${currentWidth}px`;
        container.classList.add("mermaid-diagram");

        const mermaid = (await import("mermaid")).default;

        const config: MermaidConfig = {
            startOnLoad: false,
            theme: mermaidConfig.theme || "default",
            securityLevel: "strict",
            fontFamily: "inherit",
            suppressErrorRendering: true, // Prevent error SVGs from being generated
            ...mermaidConfig,
        };

        mermaid.initialize(config);

        // First, validate the syntax using mermaid.parse()
        const parseResult = await mermaid.parse(source, {
            suppressErrors: true,
        });

        if (!parseResult) {
            // Syntax is invalid, show our custom error message
            throw new Error("Invalid Mermaid diagram syntax");
        }

        const id = `mermaid-${Math.random().toString(36).substring(2, 11)}`;
        const { svg } = await mermaid.render(id, source);

        // Create a preview text
        const previewText = document.createElement("span");
        previewText.classList.add("mermaid-preview-text");
        previewText.innerHTML = "Mermaid preview";

        // Create a wrapper to handle the transition
        const diagram = document.createElement("div");
        diagram.innerHTML = svg;

        // Replace content and fade in
        container.innerHTML = "";
        container.appendChild(previewText);
        container.appendChild(diagram);

        panzoom(diagram, {
            beforeWheel: function (e) {
                // Allow wheel-zoom only if meta or ctrl is down.
                return !(e.metaKey || e.ctrlKey);
            },
            zoomDoubleClickSpeed: 1, // Disable double-click zoom (1 = no zoom multiplier)
        });

        // Animate in the new content
        requestAnimationFrame(() => {
            // Remove size constraints after transition
            diagram.addEventListener(
                "transitionend",
                () => {
                    container.style.minHeight = "";
                    container.style.minWidth = "";
                },
                { once: true }
            );
        });
    } catch (error) {
        console.error("Failed to render diagram:", error);
        const alert = document.createElement("sl-alert");
        alert.classList.add("mermaid-error");
        alert.variant = "danger";
        alert.open = true;
        alert.innerHTML = `
            Invalid Mermaid diagram.<br/>
            <small><i>${error}</i></small>
        `;

        container.innerHTML = "";
        container.appendChild(alert);
    }
}

// Create a map to store debounced render functions for each diagram
const debouncedRenderFunctions = new Map<
    string,
    DebouncedFunc<typeof renderMermaidDiagram>
>();

export class MermaidExtension extends BaseExtension {
    plugins = () => {
        const mermaidPlugin = new Plugin({
            key: pluginKey,
            state: {
                init: () => {
                    const { darkMode } = useUIManager();
                    return {
                        decorations: DecorationSet.empty,
                        // Keep track of rendered diagrams
                        renderedDiagrams: new Map<
                            string,
                            {
                                container: HTMLElement;
                                source: string;
                                id: string;
                            }
                        >(),
                        darkMode: darkMode.value,
                    };
                },
                apply: (tr, pluginState) => {
                    const renderedDiagrams = pluginState.renderedDiagrams;
                    const darkMode = pluginState.darkMode;

                    // If the document didn't change, keep the same decorations
                    if (!tr.docChanged) {
                        return pluginState;
                    }

                    // Map existing decorations through the transaction
                    let decos: Decoration[] = [];

                    // Find Mermaid diagrams in the document
                    let count = 0;
                    tr.doc.descendants((node, pos) => {
                        if (
                            node.type.name === "code_block" &&
                            node.attrs.params === "mermaid" &&
                            node.textContent
                        ) {
                            count++;
                            // Create a unique key and id for this diagram
                            const key = `mermaid-${count}`;
                            const id = `diagram-${Math.random().toString(36).substring(2, 11)}`;

                            // Reuse existing rendered diagram if possible
                            let { container, source } =
                                renderedDiagrams.get(key) || {};
                            if (!container) {
                                container = document.createElement("div");
                                container.className = "mermaid-widget";
                                container.dataset.diagramId = id;

                                // Store for reuse
                                renderedDiagrams.set(key, {
                                    container,
                                    source: node.textContent,
                                    id,
                                });
                            }

                            if (node.textContent !== source) {
                                // Get or create debounced render function for this diagram
                                let debouncedRender =
                                    debouncedRenderFunctions.get(key);
                                if (!debouncedRender) {
                                    debouncedRender = debounce(
                                        renderMermaidDiagram,
                                        1000
                                    );
                                    debouncedRenderFunctions.set(
                                        key,
                                        debouncedRender
                                    );
                                }

                                // Render the diagram
                                debouncedRender(node.textContent, container, {
                                    theme: darkMode ? "dark" : "default",
                                    gantt: { useWidth: 1024 },
                                    pie: { useWidth: 1024 },
                                    themeVariables: {
                                        darkMode: darkMode,
                                    },
                                });
                            }

                            decos.push(
                                Decoration.widget(pos, () => container, {
                                    // Important: Make the decoration persistent
                                    key,
                                    side: -1,
                                })
                            );
                        }
                    });

                    // Clean up old diagrams and their debounced render functions
                    for (const [key, _] of renderedDiagrams) {
                        if (!decos.some((d) => d.spec.key === key)) {
                            renderedDiagrams.delete(key);
                            // Clean up the debounced render function
                            const debouncedRender =
                                debouncedRenderFunctions.get(key);
                            if (debouncedRender) {
                                debouncedRender.cancel();
                                debouncedRenderFunctions.delete(key);
                            }
                        }
                    }

                    return {
                        decorations: DecorationSet.create(tr.doc, decos),
                        renderedDiagrams,
                        darkMode,
                    };
                },
            },
            props: {
                decorations(state) {
                    return this.getState(state)?.decorations;
                },
            },
        });

        return [mermaidPlugin];
    };

    css = [unsafeCSS(styles)];
}
